import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
  Chip,
  Alert
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useTranslation } from 'react-i18next';

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatAmount = (amount, currency = 'EUR') => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: currency || 'EUR'
  }).format(amount || 0);
};

const getDaysLate = (dueDateString) => {
  if (!dueDateString) return 0;
  const dueDate = new Date(dueDateString);
  if (isNaN(dueDate.getTime())) return 0;
  const diff = Date.now() - dueDate.getTime();
  return Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)));
};

/**
 * Affiche la liste des payins en retard (overdue_recovery) dans une section dédiée.
 * Ne modifie pas le rendu des factures mensuelles normales.
 */
const OverduePayinsSection = ({ payins = [] }) => {
  const { t } = useTranslation('financial');

  if (!payins || payins.length === 0) return null;

  return (
    <Box>
      <Alert
        severity="warning"
        icon={<WarningAmberIcon />}
        sx={{ mb: 2, borderRadius: 2 }}
      >
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {t('overdue.sectionTitle', 'Paiements en retard — mois précédents')}
        </Typography>
        <Typography variant="caption">
          {t('overdue.sectionSubtitle', "Ces paiements n'ont pas pu être traités lors de leur cycle mensuel.")}
        </Typography>
      </Alert>

      {payins.map((payin) => {
        const daysLate = getDaysLate(payin.due_date);
        return (
          <Paper
            key={payin.id}
            elevation={0}
            sx={{
              mb: 2,
              p: 2.5,
              borderRadius: 2,
              border: '1px solid #ffcc80',
              background: '#fff8f0'
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a1a2e' }}>
                {payin.reservation?.listing?.title || t('financial.listingUnknown', 'Listing inconnu')}
              </Typography>
              {daysLate > 0 && (
                <Chip
                  icon={<AccessTimeIcon />}
                  label={t('overdue.daysLate', '{{count}} j. de retard', { count: daysLate })}
                  color="warning"
                  size="small"
                  sx={{ fontWeight: 700 }}
                />
              )}
            </Box>

            {/* Échéance originale */}
            <Box sx={{ mb: 1.5, px: 1.5, py: 0.75, borderRadius: 1, bgcolor: '#fff3e0', border: '1px solid #ffe0b2', display: 'inline-block' }}>
              <Typography variant="caption" sx={{ color: '#e65100', fontWeight: 600 }}>
                {t('overdue.originalDueDate', 'Échéance originale')} : {formatDate(payin.due_date)}
              </Typography>
            </Box>

            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" sx={{ color: '#888' }}>
                  {t('commissionPaymentSuccess.reservationPeriod', 'Période')}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatDate(payin.reservation?.start_date)} — {formatDate(payin.reservation?.end_date)}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" sx={{ color: '#888' }}>
                  {t('commissionPaymentSuccess.cashReceived', 'Montant cash reçu')}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatAmount(payin.cash_amount_received, payin.original_currency)}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" sx={{ color: '#888' }}>
                  {t('commissionPaymentSuccess.commissionRate', 'Taux de commission')}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {payin.commission_rate_percentage
                    ? `${payin.commission_rate_percentage}%`
                    : `${(payin.commission_rate * 100).toFixed(2)}%`}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" sx={{ color: '#888' }}>
                  {t('commissionPaymentSuccess.commissionAmount', 'Commission')}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#e65100' }}>
                  {formatAmount(payin.commission_amount, payin.original_currency)}
                </Typography>
              </Grid>

              {payin.taxes > 0 && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" sx={{ color: '#888' }}>
                    {t('commissionPaymentSuccess.taxes', 'Taxes')}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatAmount(payin.taxes, payin.original_currency)}
                  </Typography>
                </Grid>
              )}

              {payin.original_currency && payin.original_currency !== payin.currency ? (
                <>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 0.5 }} />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" sx={{ color: '#888' }}>
                      {t('commissionPaymentSuccess.commissionConverted', 'Montant commission')}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {payin.converted_amount_with_currency || formatAmount(payin.converted_amount, payin.currency)}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" sx={{ color: '#888' }}>
                      {t('commissionPaymentSuccess.taxesConverted', 'Taxes converties')}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatAmount(
                        Number(((payin.exchange_rate ? payin.taxes / payin.exchange_rate : payin.taxes) || 0).toFixed(2)),
                        payin.currency
                      )}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" sx={{ color: '#888' }}>
                      {t('commissionPaymentSuccess.totalConverted', 'Total dû converti')}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {formatAmount(
                        Number((payin.converted_amount || 0)) +
                          Number(((payin.exchange_rate ? payin.taxes / payin.exchange_rate : payin.taxes) || 0).toFixed(2)),
                        payin.currency
                      )}
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="caption" sx={{ color: '#aaa', fontStyle: 'italic' }}>
                      {t('commissionPaymentSuccess.exchangeRateExplanation')} (1 {payin.currency} = {payin.exchange_rate} {payin.original_currency})
                    </Typography>
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 0.5 }} />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" sx={{ color: '#888' }}>
                      {t('invoiceDetail.totalCommissionTaxesEuros', 'Montant total dû (commission + taxes) en EUR')}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {formatAmount(
                        Number((payin.commission_amount || 0)) + Number((payin.taxes || 0)),
                        'EUR'
                      )}
                    </Typography>
                  </Grid>
                </>
              )}
            </Grid>
          </Paper>
        );
      })}
    </Box>
  );
};

export default OverduePayinsSection;
